import { useState } from "react";
import { Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeUp, RevealLine } from "./AnimatedSection";
import { useLanguage } from "@/context/LanguageContext";

const IMAGES = {
  artistas: "https://images.unsplash.com/photo-1615754890634-69ac8bca7189?crop=entropy&cs=srgb&fm=jpg&q=85",
  labels: "https://images.unsplash.com/photo-1603048588665-791ca8aea617?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const TAB_PILL_TRANSITION = { type: "spring", duration: 0.5 };
const CONTENT_MOTION = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -14 },
  transition: { duration: 0.4 },
};

export const ParaQuien = () => {
  const [tab, setTab] = useState("artistas");
  const { t } = useLanguage();
  const tools = tab === "artistas" ? t.paraQuien.toolsArtists : t.paraQuien.toolsLabels;
  const caption = tab === "artistas" ? t.paraQuien.captionArtists : t.paraQuien.captionLabels;

  return (
    <section data-testid="para-quien-section" className="relative py-28 md:py-36 bg-[#0C0C0C] border-y border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <p className="text-brand-red text-xs font-mono tracking-[0.25em] font-semibold uppercase mb-4">{t.paraQuien.label}</p>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-12 max-w-2xl">
          <RevealLine text={t.paraQuien.title} />
        </h2>

        <div className="inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1 mb-12">
          {[{ id: "artistas", label: t.paraQuien.tabArtists }, { id: "labels", label: t.paraQuien.tabLabels }].map((tabItem) => (
            <button
              key={tabItem.id}
              data-testid={`para-quien-tab-${tabItem.id}`}
              onClick={() => setTab(tabItem.id)}
              className={`relative px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${
                tab === tabItem.id ? "text-white" : "text-neutral-400 hover:text-white"
              }`}
            >
              {tab === tabItem.id && (
                <motion.span layoutId="para-quien-pill" className="absolute inset-0 bg-gradient-to-r from-brand-red to-brand-blue rounded-full" transition={TAB_PILL_TRANSITION} />
              )}
              <span className="relative z-10">{tabItem.label}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              {...CONTENT_MOTION}
              data-testid="para-quien-content"
              className="flex flex-col gap-4"
            >
              {tools.map((tool) => (
                <div key={tool} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <Check className="w-4 h-4 text-brand-blue mt-0.5 shrink-0" strokeWidth={2.5} />
                  <p className="text-neutral-300 text-sm leading-relaxed">{tool}</p>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>

          <FadeUp className="relative rounded-3xl overflow-hidden aspect-[4/3] border border-white/10">
            <img src={IMAGES[tab]} alt={tab} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <span className="absolute bottom-6 left-6 font-heading text-white font-bold text-lg">{caption}</span>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
